/**
 * "A new version is ready" — the visible half of the update loop.
 *
 * WHY A PROMPT AND NOT JUST THE SILENT RELOAD
 * -------------------------------------------
 * sw-register reloads on its own, but ONLY when nothing is in progress and the
 * tab is hidden (see app-busy.ts). That is deliberately conservative, and the
 * cost of being conservative is that a student who keeps one tab open, mid-drill,
 * for a long session may never hit a safe moment. They then keep running old
 * code indefinitely.
 *
 * That is not hypothetical. Twenty-seven production errors in a week came from
 * two builds that were no longer served, and zero from the build that was live.
 * The bug they reported had been fixed days earlier; nothing moved those tabs
 * onto the fix.
 *
 * So: when an update is pending and we cannot safely take it, ASK. The student
 * decides when to refresh, which is both more honest than reloading under them
 * and more effective than waiting for a quiet moment that may not come.
 *
 * THE LIMIT, STATED PLAINLY
 * -------------------------
 * This can only reach a tab running a build that CONTAINS it. Tabs already stuck
 * on an older build have no code to show this prompt and cannot be reached at
 * all; they recover on their next cold launch. A refresh prompt is prevention
 * for the next stale build, never a cure for the current one.
 */

type Listener = () => void;

const listeners = new Set<Listener>();
let updateAvailable = false;

/** Called by sw-register when a genuinely new worker has taken control. */
export function markUpdateAvailable(): void {
  if (updateAvailable) return;
  updateAvailable = true;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* a listener must never break the update path */
    }
  });
}

export function isUpdateAvailable(): boolean {
  return updateAvailable;
}

export function subscribeUpdate(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Refresh because the student asked. They chose this, so we do not consult the
 * route gate: they may well be mid-drill and want the fix now.
 *
 * We DO still wait for any in-flight durable write to commit, because an answer
 * lost to their own refresh is just as lost. IndexedDB only guarantees the write
 * once its transaction commits, so a short drain is the difference between
 * saving the last answer and dropping it. Capped, because a wedged write must
 * not turn the refresh button into a dead control.
 */
export async function applyUpdateNow(
  pending: () => number,
  reload: () => void = () => window.location.reload(),
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<void> {
  const deadline = 12; // ~3 seconds at 250ms
  for (let i = 0; i < deadline && pending() > 0; i += 1) {
    await sleep(250);
  }
  reload();
}
